/**
 * @lines98/client/components/trophy-card — общий КАРКАС горизонтальной «витрины-трофея»
 * (ствол; примитив-фрейм для карточек Мастерской и Гномов).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (code-structure §1/§2 «ствол → ветки», owner-directive
 * 2026-06-14 «Гномы = копия Мастерской один-в-один»). И карточка зверя (`jewelry-card`),
 * и карточка гнома (`gnome-card`) — ОДНА И ТА ЖЕ горизонтальная плашка-трофей: 3 секции
 * [герой слева] [инфо середина, тянется] [действие справа] + строка-статус под рядом.
 * Раскладка/размеры/клик-зона героя (drill-down) + affordance — ОБЩИЕ → живут здесь, НЕ
 * копируются по двум карточкам. Каждая карточка — тонкая «ветка»: наполняет три слота
 * своим содержимым (зверь+кольцо ИЛИ гном; имя+карат ИЛИ имя+прогресс+чипы) и задаёт
 * свой класс-модификатор. Так «Гномы» = копия «Мастерской» по фрейму, а отличается только
 * наполнением слотов (owner: «копия Мастерской, только гномы и без камней»).
 *
 * **Герой = единственная клик-зона drill-down (owner-directive 2026-06-10/06-14):** тап по
 * герою (зверь/гном) открывает детальную карточку, как у зверей. Семантически — `<button>`
 * с aria-label (focus/Enter/Space из коробки), `stopPropagation` чтобы клик действия НЕ
 * открывал деталь. Future-proof: при отсутствии `onOpenDetail` герой — `<div>` (не
 * интерактивен).
 *
 * SCOPE: построить DOM-каркас (row[hero · mid · end] + status), вернуть слоты + статус-API.
 * NOT IN SCOPE: содержимое слотов (ветки рисуют сами), сетевые мутации, размеры конкретного
 * героя (ветка задаёт через свой CSS — кольцо у зверя / спрайт у гнома).
 *
 * Cross-references:
 *   - components/jewelry-card.ts (ветка: герой = кольцо радуги; mid = имя/уровень/карат+бонус).
 *   - components/gnome-card.ts (ветка: герой = спрайт гнома; mid = имя/уровень/прогресс/чипы).
 *   - trophy-card.css (co-located: раскладка 3 секций + герой-зона + размеры-токены).
 */

import './trophy-card.css';

export interface TrophyCardOptions {
  /** Класс-модификатор корня (ветка): `jewelry-card` / `gnome-card`. */
  readonly variant: string;
  /** Стабильный data-атрибут для тестов/стилей (`type` зверя / `color` гнома). */
  readonly dataKey: string;
  readonly dataValue: string;
  /**
   * Открыть деталь (drill-down) — тап по герою. Опц.: без него герой не интерактивен
   * (`<div>`). aria-label героя задаёт ветка через `setHeroLabel`.
   */
  readonly onOpenDetail?: () => void;
}

export interface TrophyCardFrame {
  /** Корневой элемент карточки (ветка добавляет свой контент в слоты). */
  readonly root: HTMLElement;
  /** Секция 1 (слева): герой. `<button>` если задан `onOpenDetail`, иначе `<div>`. */
  readonly hero: HTMLElement;
  /** Секция 2 (середина, тянется): инфо. */
  readonly mid: HTMLElement;
  /** Секция 3 (справа): действие (цена + кнопка). */
  readonly end: HTMLElement;
  /** Задать aria-label героя-зоны (ветка знает имя сущности). */
  setHeroLabel(label: string): void;
  /** Показать/спрятать строку-статус под рядом секций. */
  setStatus(message: string | null, kind?: 'error' | 'info'): void;
}

/**
 * Построить каркас горизонтальной карточки-трофея. Ветка (`jewelry-card`/`gnome-card`)
 * наполняет `hero`/`mid`/`end` своим содержимым.
 */
export function createTrophyCard(opts: TrophyCardOptions): TrophyCardFrame {
  const { variant, dataKey, dataValue, onOpenDetail } = opts;

  const root = document.createElement('div');
  root.className = `trophy-card ${variant}`;
  root.setAttribute('role', 'listitem');
  root.dataset[dataKey] = dataValue;

  // --- СЕКЦИЯ 1: герой (drill-down клик-зона). `<button>` если задан колбэк → a11y
  //     (focus/Enter/Space) из коробки; иначе `<div>` (не интерактивен). ---
  const hero = onOpenDetail
    ? (document.createElement('button') as HTMLButtonElement)
    : document.createElement('div');
  hero.className = 'trophy-card-hero';
  if (onOpenDetail) {
    const heroBtn = hero as HTMLButtonElement;
    heroBtn.type = 'button';
    heroBtn.classList.add('trophy-card-zone');
    heroBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      onOpenDetail();
    });
  }

  // --- СЕКЦИЯ 2: инфо (тянется). ---
  const mid = document.createElement('div');
  mid.className = 'trophy-card-mid';

  // --- СЕКЦИЯ 3: действие. ---
  const end = document.createElement('div');
  end.className = 'trophy-card-end';

  // --- Статус вердикта (своя строка под рядом секций). ---
  const statusEl = document.createElement('div');
  statusEl.className = 'trophy-card-status';
  statusEl.setAttribute('role', 'status');
  statusEl.setAttribute('aria-live', 'polite');
  statusEl.hidden = true;

  const rowEl = document.createElement('div');
  rowEl.className = 'trophy-card-row';
  rowEl.append(hero, mid, end);
  root.append(rowEl, statusEl);

  return {
    root,
    hero,
    mid,
    end,
    setHeroLabel(label: string): void {
      if (onOpenDetail) hero.setAttribute('aria-label', label);
    },
    setStatus(message: string | null, kind: 'error' | 'info' = 'error'): void {
      if (!message) {
        statusEl.hidden = true;
        statusEl.textContent = '';
        statusEl.classList.remove('trophy-card-status--error', 'trophy-card-status--info');
        return;
      }
      statusEl.hidden = false;
      statusEl.textContent = message;
      statusEl.classList.toggle('trophy-card-status--error', kind === 'error');
      statusEl.classList.toggle('trophy-card-status--info', kind === 'info');
    },
  };
}
